import { CartesianScaleOptions, ChartDataset, ChartOptions, ChartTypeRegistry, DefaultDataPoint } from 'chart.js'
import { store } from '../../store/Store'

type Datastore<TType extends keyof ChartTypeRegistry, TData> = ChartDataset<TType, TData> & { plan: boolean }

const ChartsHelper = {
    loadingColors: () => {
        return [
            '#7F7F7F',
            '#696969',
            '#555555',
            '#4A4A4A',
            '#414141',
            '#383838',
            '#2D2D2D',
            '#1A1A1A'
        ]
    },
    colorsFromTheme: () => {
        return [
            '#0575e6',
            '#0558c7',
            '#063ca8',
            '#042089',
            '#00006a',
            '#03dac5',
            '#00c7db',
            '#00b0ee',
            '#0095f4'
        ]
    },
    distinctColors: () => {
        return [
            '#003f5c',
            '#2f4b7c',
            '#665191',
            '#a05195',
            '#d45087',
            '#f95d6a',
            '#ff7c43',
            '#ffa600'
        ]
    },
    applyColors: <TType extends keyof ChartTypeRegistry, TData>(datastores: Datastore<TType, TData>[], isLoading?: boolean) => {
        if (datastores) {
            const colors = isLoading ? ChartsHelper.loadingColors() : ChartsHelper.colorsFromTheme()

            for (let i = 0; i < datastores.length; i++) {
                const datastore = datastores[i]
                const color = colors[i % 8]

                if (!(datastore as any).backgroundColor)
                    (datastore as any).backgroundColor = color
                if (!datastore.borderColor)
                    datastore.borderColor = color
            }
        }
    },
    applyColorsToPieChart: <TType extends keyof ChartTypeRegistry, TData>(datasets: ChartDataset<TType, TData>[], isLoading?: boolean) => {
        const colors = isLoading ? ChartsHelper.loadingColors() : ChartsHelper.colorsFromTheme()

        for (let i = 0; i < datasets?.length; i++) {
            const dataset = datasets[i];
            (dataset as any).backgroundColor = (dataset.data as Array<number>).map((_, j) => colors[j % 8])
            dataset.borderColor = (dataset.data as Array<number>).map((_, j) => colors[j % 8])
        }
    },
    applyMode: (options: ChartOptions) => {
        if (!options) return

        const mode = store.getState().app.mode
        const fontColor = mode === 'dark' ? 'rgba(255,255,255,.87)' : 'rgba(0, 0, 0, 0.87)'

        if (options.scales && options.scales.x) {
            if (options.scales.x.ticks) options.scales.x.ticks.color = fontColor
            else options.scales.x.ticks = { color: fontColor }

            // @ts-expect-error The typing that we import is not correct with the latest release
            if (options.scales.x.border) options.scales.x.border.color = fontColor
            // @ts-expect-error The typing that we import is not correct with the latest release
            else options.scales.x.border = { color: fontColor }
        }

        const applyStilesToY = (scale: CartesianScaleOptions | any) => {
            if (scale.ticks) scale.ticks.color = fontColor
            else scale.ticks = { color: fontColor }

            if (options.indexAxis !== 'y') {
                if (scale.border) scale.border.color = fontColor
                else scale.border = { color: fontColor }
            }
        }

        if (options.scales && options.scales.y) applyStilesToY(options.scales.y as any)
        if (options.scales && options.scales.y2) applyStilesToY(options.scales.y2 as any)

        if (options.plugins) {
            if (options.plugins.legend) {
                if (options.plugins.legend.labels) options.plugins.legend.labels.color = fontColor
                else options.plugins.legend.labels = { color: fontColor }
            } else options.plugins.legend = { labels: { color: fontColor } }
        }
    }
}

export default ChartsHelper

export type ChartDatasetWithUnit<TType extends keyof ChartTypeRegistry = keyof ChartTypeRegistry, TData = DefaultDataPoint<TType>> =
    ChartDataset<TType, TData> & { unit?: string }

export interface ChartDataWithUnit<TType extends keyof ChartTypeRegistry = keyof ChartTypeRegistry, TData = DefaultDataPoint<TType>, TLabel = unknown> {
    datasets: ChartDatasetWithUnit<TType, TData>[]
    labels?: TLabel[]
}
